import { useMutation, useQueryClient } from '@tanstack/react-query';

import { importWorkflow } from '@/lib/api/generated/api';
import type { WorkflowImportResult } from '@/lib/api/generated/model';
import type { AnswersRequest } from '../upload/answers';
import { invalidateWorkspace } from '@/features/workspaces';
import { WorkflowRejectedError } from '../errors';

/**
 * Uploads a workflow YAML file to the workspace's import operation.
 *
 * The file travels as-is — parsing and validation are server-side and
 * authoritative (spec D-Q1); nothing here inspects the content. A rejection
 * (409 conflict, 413 too large, 422 invalid) throws `WorkflowRejectedError`
 * carrying every violation for the dialog to render.
 *
 * Success invalidates the workspace aggregate — the one read every surface
 * renders from — which is what makes the new workflow appear in the list and
 * the boards list immediately, without a reload (FR-048/049).
 */
export function useImportWorkflow(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // Returns the whole result, not just the workflow: an import can succeed and
    // still have let something go — an assignee naming somebody this environment
    // does not have (spec 067 FR-034). Dropping the warnings here would make that
    // silent, which is the failure the warning exists to prevent.
    mutationFn: async ({
      file,
      answers,
    }: {
      file: File;
      /**
       * What to use in place of references the file makes (spec 102 US2).
       *
       * Omitted entirely when nothing was answered, rather than sent empty: an
       * upload of a file that resolves must be the request it has always been,
       * and an empty statement is a different statement from no statement.
       */
      answers?: AnswersRequest;
    }): Promise<WorkflowImportResult> => {
      const response = await importWorkflow(workspaceId, {
        file,
        ...(answers ? { substitutions: JSON.stringify(answers) } : {}),
      });
      if (response.status === 201) return response.data;
      if (
        response.status === 409 ||
        response.status === 413 ||
        response.status === 422
      ) {
        throw new WorkflowRejectedError(response.status, response.data);
      }
      throw new Error('The workflow could not be imported.');
    },
    onSuccess: () => invalidateWorkspace(queryClient, workspaceId),
  });
}
